import { createContext, useContext, useEffect, useState } from "react";
import { cartCreate, cartLinesAdd, cartLinesRemove, cartLinesUpdate, fetchCart, fetchProducts } from "@/lib/shopify";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const CART_KEY = "almo_cart_id";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [products, setProducts] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchProducts()
      .then((data) => {
        const map = {};
        data.products.edges.forEach(({ node }) => {
          const localId = node.handle.includes("vanilla") ? "vanilla" : node.handle.includes("chocolate") ? "chocolate" : null;
          const variant = node.variants.edges[0]?.node;
          if (localId && variant) {
            map[localId] = { variantId: variant.id, price: variant.price.amount, title: node.title };
          }
        });
        setProducts(map);
      })
      .catch((e) => console.error("Shopify products failed:", e));

    const savedId = localStorage.getItem(CART_KEY);
    if (savedId) {
      fetchCart(savedId)
        .then((data) => data.cart && setCart(data.cart))
        .catch(() => localStorage.removeItem(CART_KEY));
    }
  }, []);

  const applyCart = (data, key) => {
    const result = data[key];
    if (result.userErrors?.length) throw new Error(result.userErrors[0].message);
    if (result.cart) {
      setCart(result.cart);
      localStorage.setItem(CART_KEY, result.cart.id);
    }
  };

  const addItem = async (localId, quantity = 1) => {
    const sp = products[localId];
    if (!sp) return;
    setBusy(true);
    try {
      const lines = [{ merchandiseId: sp.variantId, quantity }];
      if (cart?.id) {
        applyCart(await cartLinesAdd(cart.id, lines), "cartLinesAdd");
      } else {
        applyCart(await cartCreate(lines), "cartCreate");
      }
      setDrawerOpen(true);
    } catch (e) {
      console.error("Add to cart failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const addItems = async (localIds) => {
    const lines = localIds
      .map((id) => products[id])
      .filter(Boolean)
      .map((sp) => ({ merchandiseId: sp.variantId, quantity: 1 }));
    if (!lines.length) return;
    setBusy(true);
    try {
      if (cart?.id) {
        applyCart(await cartLinesAdd(cart.id, lines), "cartLinesAdd");
      } else {
        applyCart(await cartCreate(lines), "cartCreate");
      }
      setDrawerOpen(true);
    } catch (e) {
      console.error("Add bundle failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const updateLine = async (lineId, quantity) => {
    if (!cart?.id) return;
    setBusy(true);
    try {
      if (quantity <= 0) {
        applyCart(await cartLinesRemove(cart.id, [lineId]), "cartLinesRemove");
      } else {
        applyCart(await cartLinesUpdate(cart.id, [{ id: lineId, quantity }]), "cartLinesUpdate");
      }
    } catch (e) {
      console.error("Update cart failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const removeLine = (lineId) => updateLine(lineId, 0);

  return (
    <CartContext.Provider
      value={{ cart, products, drawerOpen, setDrawerOpen, busy, addItem, addItems, updateLine, removeLine }}
    >
      {children}
    </CartContext.Provider>
  );
}
